import React, { useEffect, useRef, useState } from 'react'

interface OverlayInfoProps {
  moveIntervalInSeconds?: number
  zIndex?: number
  children?: React.ReactNode
}

type OverlayInfoTextProps = Omit<OverlayInfoProps, 'children'> & {
  text: string
}

type OverlayInfoBannerProps = Omit<OverlayInfoProps, 'children'> & {
  text: string
  logoUrl?: string
  logoPosition?: 'left' | 'right'
  bannerStyle?: 'horizontal' | 'vertical'
  backgroundColor?: string
  textColor?: string
}

export const OverlayInfo = ({ moveIntervalInSeconds = 0, zIndex = 1000, children }: OverlayInfoProps) => {
  const [top, setTop] = useState(50)
  const [left, setLeft] = useState(50)
  const infoRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (moveIntervalInSeconds <= 0) return
    const moveInfo = () => {
      setTop(Math.round(Math.random() * 60) + 20)
      setLeft(Math.round(Math.random() * 60) + 20)
    }
    const intervalHandle = window.setInterval(moveInfo, moveIntervalInSeconds * 1000)
    return (): void => {
      window.clearInterval(intervalHandle)
    }
  }, [moveIntervalInSeconds])

  return (
    <div
      ref={infoRef}
      className="absolute rounded-lg"
      style={{
        top: `${top}%`,
        left: `${left}%`,
        transform: 'translate(-50%, -50%)',
        zIndex,
      }}
    >
      {children}
    </div>
  )
}

export const OverlayInfoText = ({ text, moveIntervalInSeconds, zIndex }: OverlayInfoTextProps) => {
  return (
    <OverlayInfo moveIntervalInSeconds={moveIntervalInSeconds} zIndex={zIndex}>
      <div className="px-8 py-6 bg-black/70 rounded-lg text-center">
        <p className="text-white text-3xl font-medium">{text}</p>
      </div>
    </OverlayInfo>
  )
}

export const OverlayInfoBanner = ({
  text,
  logoUrl,
  backgroundColor = 'bg-black/70',
  textColor = 'text-white',
  moveIntervalInSeconds,
  zIndex,
}: OverlayInfoBannerProps) => {
  return (
    <OverlayInfo moveIntervalInSeconds={moveIntervalInSeconds} zIndex={zIndex}>
      <div
        className={`w-[560px] min-h-[175px] px-10 py-8 ${backgroundColor} rounded-lg shadow-lg flex flex-col items-center justify-center gap-5`}
      >
        <div className="flex-1 flex items-center">
          <p className={`${textColor} text-xl font-medium text-center leading-tight`}>{text}</p>
        </div>

        {logoUrl && (
          <div className="flex-shrink-0">
            <img src={logoUrl} alt="Logo" className="h-12 w-auto object-contain" />
          </div>
        )}
      </div>
    </OverlayInfo>
  )
}
